import { useContext, type ButtonHTMLAttributes, type MouseEvent, type PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Box, HStack, VStack, type BoxProps } from '../Core';
import { Icon, IconName } from '../Icons';
import { usePowerSearchContext } from './PowerSearchContext';
import { PowerSearchHighlightContext } from './PowerSearchHighlightContext';

type PowerSearchItemProps = PropsWithChildren<
  Omit<BoxProps, 'children' | 'id' | 'onClick'> &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'id' | 'onClick' | 'type'> & {
      id: string;
      onSelect?(id: string): void;
    }
>;

export function PowerSearchItem({ children, id, onSelect, ...props }: PowerSearchItemProps) {
  const highlightedID = useContext(PowerSearchHighlightContext);
  const isHighlighted = id === highlightedID;
  const {
    modal: { close },
  } = usePowerSearchContext();

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    close();
    onSelect?.(id);
  };

  return (
    <Wrapper
      id={id}
      data-testid="power-search-item"
      data-highlighted={isHighlighted}
      className="power-search-item"
      onClick={handleClick}
      $isHighlighted={isHighlighted}
      {...props}
    >
      <HStack justifyContent="space-between">
        <VStack w="100%" alignItems="flex-start">
          {children}
        </VStack>
        {isHighlighted && <Icon icon={IconName.ChevronRight} />}
      </HStack>
    </Wrapper>
  );
}

type WrapperProps = Omit<BoxProps, 'onClick'> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    $isHighlighted: boolean;
  };

const Wrapper = styled(Box).attrs<WrapperProps>({
  as: 'button',
  type: 'button',
})<WrapperProps>`
  background: var(--colors-gray-020);
  appearance: none;
  border: 0;
  display: block;
  text-align: initial;
  background: var(--colors-gray-020);
  color: var(--colorTextDefault);
  outline: none;

  ${({ $isHighlighted }) => $isHighlighted && `background: var(--colors-gray-030)`};
  &:hover {
    background: var(--colors-gray-030);
  }
  &:hover {
    cursor: pointer;
  }
`;
Wrapper.defaultProps = {
  p: 'spacing8',
};
